import { useCallback, useEffect, useId, useState } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { QuizAnswer, QuizResults, QuizTemplate } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import {
  getBackgroundStyle,
  getBorderStyle,
  getFinalAccentColor,
  getReadableTextColor,
} from '@/utils/colors';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  fetchMyPublicQuizSubmission,
  postAnalyticsEvent,
  submitPublicQuiz,
  submitPublicQuizPut,
} from '@/features/live/liveThunks';
import { Button } from '@/components/ui/button';
import { toast } from 'react-toastify';
import { useChannelName } from '@/routes/AblyAuth';
import type { Message } from 'ably';
import AblyChannelListener from '@/components/common/AblyChannelListener';
import { parsePresenceUpdateLiveCount } from '@/lib/presenceUpdate';
import { useWebinarT } from '@/hooks/useWebinarT';
type QuizCardProps = {
  quiz: QuizTemplate;
  visualIdentity: WorkspaceVisualIdentity;
  isAttendee?: boolean;
  isAdmin?: boolean;
  isModerator?: boolean;
  isShow?: boolean;
  showResults?: boolean;
  webinarId?: number;
  sessionId?: number;
  authorization?: string;
  sessionIdentifier?: string;
  quizResults?: QuizResults | null;
  liveParticipantCountSync?: number;
  // Salle de test : la réponse reste locale, ni envoi ni analytics.
  onLocalSubmit?: (answer: { questionId: number; optionIds: number[] }) => void;
};

export default function QuizCard({
  quiz,
  visualIdentity,
  isAttendee = false,
  isAdmin = false,
  isModerator = false,
  isShow = false,
  showResults = false,
  webinarId,
  sessionId,
  authorization,
  sessionIdentifier,
  quizResults: quizResultsProp,
  liveParticipantCountSync,
  onLocalSubmit,
}: QuizCardProps) {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const uniqueId = useId();
  const question = quiz?.questions?.[0];
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const publicQuizSubmission = useAppSelector((s: RootState) => s.live.publicQuizSubmission);
  const [quizResultsFromAbly, setQuizResultsFromAbly] = useState<QuizResults | null>(null);
  const [liveParticipantCountFromChannel, setLiveParticipantCountFromChannel] = useState(0);

  const channelName = useChannelName();
  const handleChannelMessage = (message: Message) => {
    if (!channelName || !message) return;

    const normalizedMessages =
      message.name === 'batch-events' && Array.isArray(message.data)
        ? message.data.filter(
            (event): event is { name: string; data: unknown } =>
              !!event &&
              typeof event === 'object' &&
              typeof (event as { name?: unknown }).name === 'string',
          )
        : [{ name: message.name, data: message.data }];

    for (const currentMessage of normalizedMessages) {
      if (currentMessage.name === 'presence-update') {
        const live = parsePresenceUpdateLiveCount(currentMessage.data);
        if (live !== null) {
          setLiveParticipantCountFromChannel(live);
        }
        continue;
      }
      if (currentMessage.name !== 'quiz-results-update') continue;
      if (!quiz?.id || !webinarId) continue;
      const data = currentMessage.data as
        | { webinarId?: number; templateId?: number; answers?: unknown[]; questions?: unknown[] }
        | undefined;
      const hasQuestions = Array.isArray(data?.questions);
      const hasAnswers = Array.isArray(data?.answers);
      if (data?.templateId === quiz.id && data?.webinarId != null && (hasQuestions || hasAnswers)) {
        setQuizResultsFromAbly(data as QuizResults);
      }
    }
  };
  const channelListener = channelName ? (
    <AblyChannelListener channelName={channelName} onMessage={handleChannelMessage} />
  ) : null;

  const quizResults = isShow ? quizResultsFromAbly : quizResultsProp;
  const liveParticipantCount = Math.max(
    liveParticipantCountSync ?? 0,
    liveParticipantCountFromChannel,
  );
  const colorsArray = [
    {
      id: 'accent',
      name: "Couleur d'accent",
      value: visualIdentity?.accentColor,
      color: visualIdentity?.accentColor,
    },
    {
      id: 'background',
      name: 'Couleur de fond',
      value: visualIdentity?.backgroundColor,
      color: visualIdentity?.backgroundColor,
    },
    {
      id: 'contrast',
      name: 'Couleur de contraste',
      value: visualIdentity?.contrastColor,
      color: visualIdentity?.contrastColor,
    },
    {
      id: 'gradient1-color1',
      name: 'Gradient 1',
      value: visualIdentity?.gradient1Color1,
      color: visualIdentity?.gradient1Color1,
    },

    {
      id: 'gradient2-color1',
      name: 'Gradient 2',
      value: visualIdentity?.gradient2Color1,
      color: visualIdentity?.gradient2Color1,
    },

    {
      id: 'secondary-background',
      name: 'Fond secondaire',
      value: visualIdentity?.secondaryBackgroundColor,
      color: visualIdentity?.secondaryBackgroundColor,
    },
    { id: 'black', name: 'Noir', value: '#000000', color: '#000000' },
    { id: 'white', name: 'Blanc', value: '#FFFFFF', color: '#FFFFFF' },
  ];
  const hasExistingSubmission =
    publicQuizSubmission &&
    publicQuizSubmission?.templateId === quiz?.id &&
    question &&
    publicQuizSubmission?.answers?.some((a) => a?.questionId === question?.id);

  const isDisabled = hasSubmitted || !!hasExistingSubmission;

  useEffect(() => {
    if (!isAttendee || !question || !publicQuizSubmission) {
      return;
    }

    const existingAnswer = publicQuizSubmission?.answers?.find(
      (a) => a?.questionId === question?.id,
    );

    if (existingAnswer) {
      setSelectedOptionId(existingAnswer.optionId);
      setHasSubmitted(true);
    }
  }, [isAttendee, question, publicQuizSubmission]);

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return 'transparent';
    }
    if (colorId === 'custom') {
      return customColor || '#FFFFFF';
    }
    if (colorId && /^#[0-9A-Fa-f]{6}$/.test(colorId)) {
      return colorId;
    }
    const color = colorsArray.find((c) => c.id === colorId);
    return color?.value || '#FFFFFF';
  };
  const getFinalColor = (colorType: 'backgroundColor' | 'borderColor' | 'accentColor') => {
    const colorId = quiz[colorType];
    return getColorValueById(colorId || 'contrast');
  };

  const getOptionPercentage = useCallback(
    (optionId: number): number => {
      const canSeeResults = isAdmin || isModerator || showResults || !!quizResults;
      if (!question || !canSeeResults || !quizResults || quizResults.templateId !== quiz?.id) {
        return 0;
      }
      // Format API : questions[].options[].count
      if (Array.isArray(quizResults.questions)) {
        const questionResult = quizResults.questions.find((q) => q?.questionId === question?.id);
        if (!questionResult) return 0;
        const optionResult = questionResult?.options?.find((opt) => opt?.optionId === optionId);
        if (!optionResult) return 0;
        const totalResponses =
          questionResult?.options?.reduce((sum, opt) => sum + (opt?.count ?? 0), 0) ?? 0;
        if (totalResponses === 0) return 0;
        return Math.round(((optionResult?.count ?? 0) / totalResponses) * 100);
      }
      // Format Ably (legacy) : answers[] flat
      const answers = (quizResults as { answers?: QuizAnswer[] })?.answers;
      if (Array.isArray(answers)) {
        const answersForQuestion = answers.filter((a) => a?.questionId === question?.id);
        const total = answersForQuestion.length;
        if (total === 0) return 0;
        const count = answersForQuestion.filter((a) => a?.optionId === optionId).length;
        return Math.round((count / total) * 100);
      }
      return 0;
    },
    [isAdmin, showResults, quizResults, quiz?.id, question?.id],
  );
  const handleChange = useCallback(
    (value: string) => {
      if (isDisabled) return;

      const optionId = Number(value);
      const selectedOption = question?.options?.find((opt) => opt?.id === optionId);
      if (!selectedOption) return;

      setSelectedOptionId(optionId);
    },
    [isDisabled, question],
  );

  const handleSubmit = useCallback(() => {
    if (onLocalSubmit) {
      if (!isAttendee || !question || selectedOptionId == null || isDisabled) return;
      setHasSubmitted(true);
      toast.success(t('live.quiz.thankYou'));
      onLocalSubmit({ questionId: question.id, optionIds: [selectedOptionId] });
      return;
    }
    if (
      !isAttendee ||
      !webinarId ||
      !sessionId ||
      !authorization ||
      selectedOptionId == null ||
      isDisabled ||
      isSubmitting
    ) {
      return;
    }

    const input = {
      sessionId,
      answers: [
        {
          questionId: question?.id,
          optionIds: [selectedOptionId],
        },
      ],
    };

    const action = hasSubmitted ? submitPublicQuizPut : submitPublicQuiz;

    setIsSubmitting(true);
    dispatch(
      action({
        webinarId,
        templateId: quiz?.id,
        authorization,
        input,
      }),
    )
      .unwrap()
      .then((submission) => {
        setHasSubmitted(true);
        toast.success(t('live.quiz.thankYou'));
        // accepté par live-api : pas encore persisté, et le consumer produit QUIZ_SUBMISSION
        if (submission === null) return;
        if (webinarId && authorization) {
          dispatch(
            fetchMyPublicQuizSubmission({
              webinarId,
              templateId: quiz?.id,
              authorization,
            }),
          );
        }
        if (sessionIdentifier) {
          dispatch(
            postAnalyticsEvent({
              webinarId,
              sessionIdentifier,
              authorization,
              input: {
                eventType: 'QUIZ_SUBMISSION',
                payloadJson: JSON.stringify({
                  templateId: quiz?.id,
                  questionId: question.id,
                  optionIds: [selectedOptionId],
                  sessionId,
                }),
                occurredAt: new Date().toISOString(),
              },
            }),
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    isAttendee,
    webinarId,
    sessionId,
    authorization,
    selectedOptionId,
    isDisabled,
    isSubmitting,
    hasSubmitted,
    question,
    quiz?.id,
    sessionIdentifier,
    dispatch,
    onLocalSubmit,
  ]);

  if (!question) {
    return channelListener;
  }
  const canSeeResults = isAdmin || isModerator || showResults || !!quizResults;
  const questionResult = quizResults?.questions?.find((q) => q?.questionId === question?.id);
  const totalResponses =
    questionResult?.options?.reduce((sum, opt) => sum + (opt?.count ?? 0), 0) ?? 0;
  const participationPercentage =
    liveParticipantCount > 0
      ? Math.min(100, Math.round((totalResponses / liveParticipantCount) * 100))
      : 0;
  return (
    <div
      className={`flex flex-col gap-4 w-full transition-opacity ${isDisabled ? 'opacity-60 pointer-events-none' : ''}`}
    >
      {channelListener}
      <div
        className="flex flex-col w-full gap-4 rounded-lg border-2 p-4 max-w-[607px] mx-auto"
        style={{
          ...getBackgroundStyle(quiz?.backgroundColor || 'accent', undefined, visualIdentity),
          ...getBorderStyle(quiz?.borderColor || 'accent', undefined, visualIdentity),
        }}
      >
        <div className="flex items-center gap-2">
          <h3
            className="text-base lg:text-lg font-semibold"
            style={{
              color: quiz?.titleColor || '#000000',
            }}
          >
            {question?.textContent ?? ''}
          </h3>
        </div>

        <RadioGroup
          value={selectedOptionId ? selectedOptionId.toString() : ''}
          onValueChange={handleChange}
          disabled={!isAttendee || isDisabled}
          className={isDisabled ? 'cursor-not-allowed' : ''}
        >
          {question?.options?.map((answer) => {
            const shouldShowResults = quizResults ? true : (isAdmin && isShow) || showResults;
            const percentage = shouldShowResults ? getOptionPercentage(answer?.id ?? 0) : 0;
            const optionId = `${uniqueId}-${quiz?.id}-${answer?.id}`;
            return (
              <Label
                key={answer?.id}
                htmlFor={optionId}
                className={`relative flex items-center space-x-2 p-2 rounded-lg bg-white border border-input ${
                  isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {shouldShowResults && (
                  <div
                    className="absolute inset-0 transition-all duration-300 ease-in-out rounded-lg pointer-events-none"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: getFinalAccentColor(
                        quiz?.accentColor || 'contrast',
                        undefined,
                        visualIdentity,
                      ),
                      opacity: 0.3,
                    }}
                  />
                )}
                <RadioGroupItem
                  value={answer?.id?.toString() ?? ''}
                  id={optionId}
                  style={
                    {
                      '--accent-color': getFinalColor('accentColor'),
                    } as React.CSSProperties
                  }
                  className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                />
                <span className="text-sm text-black relative z-10">
                  {answer?.textContent ?? ''}
                  {shouldShowResults && <span className="text-tertiary ml-2">{percentage}%</span>}
                </span>
              </Label>
            );
          })}
        </RadioGroup>

        {isAttendee && ((webinarId && sessionId && authorization) || onLocalSubmit) && (
          <Button
            onClick={handleSubmit}
            disabled={selectedOptionId == null || isDisabled || isSubmitting}
            className="w-full"
            style={{
              backgroundColor: getFinalColor('accentColor'),
              color: getReadableTextColor(getFinalColor('accentColor')),
            }}
          >
            {t(isSubmitting ? 'live.quiz.sending' : 'live.quiz.submit')}
          </Button>
        )}
        {canSeeResults && participationPercentage > 0 && (
          <span
            style={{
              color: quiz?.titleColor ?? '#000000',
            }}
            className="font-normal text-sm ml-1"
          >
            {liveParticipantCount > 0 &&
              `${t('live.participationPercentage', { percentage: participationPercentage })}`}
          </span>
        )}
      </div>
    </div>
  );
}
